import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import api, { VOTE_API_RESPONSE_CODE } from 'util/api';
import { checkIfTrue } from 'util/helpers';

interface CustomIdleTimerOptions {
  hasLoggedInFromStorage: boolean;
  onIdle(): Promise<void>;
}

const MINUTES_TO_MS = 60000;

const SESSION_TIMEOUT = 90 * MINUTES_TO_MS;
const ACTIVITY_THROTTLE = 25 * MINUTES_TO_MS;

const CustomIdleTimerContext = React.createContext(() => {});
export { CustomIdleTimerContext };

const useCustomIdleTimer = ({ hasLoggedInFromStorage = false, onIdle }: CustomIdleTimerOptions) => {
  const userData = useSelector((state: RootState) => state.user.data);
  const cmsSettings = useSelector((state: RootState) => state.widget.cms.settings);
  const { code = null, token = null } = userData || {};
  const { apiKey = null, version_id: versionId = null, window_status = '0' } = cmsSettings;
  const isWindowOpen = checkIfTrue(window_status);
  const [idle, setIdle] = useState(false);
  const [isFirstHeartbeat, setIsFirstHeartbeat] = useState(true);
  const callbackRef = useRef(onIdle);

  // Update callback (logout) function reference
  useEffect(() => {
    callbackRef.current = onIdle;
  }, [onIdle]);

  // Handle user logout. Reset local state
  useEffect(() => {
    if (!code) {
      setIsFirstHeartbeat(true);
    }
  }, [code]);

  // Idle effect. Executes callback
  useEffect(() => {
    if (idle) {
      callbackRef.current();
    }
  }, [idle, callbackRef]);

  // Heartbeat function
  const heartbeat = useCallback(
    async function (apiKey: string, versionId: string, code: string, token: string) {
      const response = await api.updateActiveSession(apiKey, versionId, code, token);
      if (response && response.response_code !== VOTE_API_RESPONSE_CODE.VALID) {
        callbackRef.current();
      }
    },
    [callbackRef]
  );

  // Handle user going idle.
  const handleIdle = useCallback(() => {
    setIdle(true);
  }, [setIdle]);

  // Handle user becoming active.
  const handleActive = useCallback(() => {
    setIdle(false);
  }, [setIdle]);

  // Handle regular user activity. Debounced.
  const handleAction = useCallback(() => {
    // No need to fire a heartbeat immediately after login unless we're logged in from localStorage
    if (code && isFirstHeartbeat && !hasLoggedInFromStorage) {
      setIsFirstHeartbeat(false);
      return;
    }

    if (apiKey && code && token && versionId !== null && isWindowOpen) {
      heartbeat(apiKey, versionId, code, token);
    }

    setIdle(false);
  }, [
    setIsFirstHeartbeat,
    isFirstHeartbeat,
    heartbeat,
    apiKey,
    code,
    token,
    versionId,
    isWindowOpen,
    hasLoggedInFromStorage,
  ]);

  const { reset } = useIdleTimer({
    timeout: SESSION_TIMEOUT,
    onIdle: handleIdle,
    onActive: handleActive,
    onAction: handleAction,
    throttle: ACTIVITY_THROTTLE,
    crossTab: {
      emitOnAllTabs: true,
    },
  });

  return {
    resetIdleTimer: reset,
  };
};

export default useCustomIdleTimer;
